import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import useGuardedRealtimeList from '../realtime/useGuardedRealtimeList';
import { 
  setTasks, 
  replaceAllTaskComments,
  setLoading, 
  setError, 
  addTask, 
  updateTask as updateTaskAction, 
  deleteTask as deleteTaskAction,
  setTaskComments,
  addTaskComment
} from "../redux/slices/engineeringTasksSlice";
import { selectApiUrls } from "../redux/slices/apiUrlSlice";
import { 
  fetchData, 
  postData, 
  patchData, 
  deleteData 
} from "../redux/slices/apiCallSlice";

const read = async (dispatch, args) => {
  const request = dispatch(fetchData(args));
  try { return await request.unwrap(); }
  finally { request.unsubscribe?.(); }
};

/**
 * Custom hook for managing engineering tasks
 * Provides functions to fetch, create, update, and delete tasks
 */
export const useEngineeringTasks = ({ assignedTo, commentsEnabled = false } = {}) => {
  const dispatch = useDispatch();
  const store = useStore();
  const apiUrls = useSelector(selectApiUrls);
  const user = useSelector(state => state.user?.userDetails);
  const userKey = user?.localAccountId || user?.username;
  const { tasks, comments, loading, error, filters, sortBy, sortOrder } = useSelector(
    (state) => state.engineeringTasks
  );
  const [active, setActive] = useState(false);
  const ownerId = useId();
  const [userList, setUserList] = useState({ key: null, rows: [] });
  const scoped = assignedTo !== undefined;
  const scopeKey = JSON.stringify([apiUrls.engineeringTasks, user?.localAccountId || user?.username, assignedTo]);
  const current = useRef({});
  current.current = { scopeKey, user, assignedTo };
  const mounted = useRef(true);
  const userRequest = useRef(0);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useGuardedRealtimeList({ apiKey: 'engineeringTasks', enabled: active && (!scoped || Boolean(assignedTo)),
    ownerSuffix: scoped ? ownerId : '',
    params: scoped ? { assignedTo } : undefined,
    apply: rows => scoped ? setUserList({ key: scopeKey, rows }) : dispatch(setTasks(rows)),
  });
  useGuardedRealtimeList({ apiKey: 'engineeringTaskComments', enabled: commentsEnabled && active,
    blockDialogs: false, apply: rows => dispatch(replaceAllTaskComments(rows)),
  });

  /**
   * Fetch all engineering tasks
   */
  const fetchTasks = useCallback(async () => {
    setActive(true);
    try {
      dispatch(setLoading(true));
      const result = await read(dispatch, { url: apiUrls.engineeringTasks });
      const currentUser = store.getState().user?.userDetails;
      if (mounted.current && store.getState().apiUrls?.urls?.engineeringTasks === apiUrls.engineeringTasks &&
        (currentUser?.localAccountId || currentUser?.username) === userKey) {
        dispatch(setTasks(result));
      }
    } catch (err) {
      console.error("Error fetching engineering tasks:", err);
      dispatch(setError(err.message || "Failed to fetch tasks"));
    }
  }, [dispatch, store, apiUrls.engineeringTasks, userKey]);

  /**
   * Fetch tasks for a specific user
   */
  const fetchUserTasks = useCallback(async (assignedTo) => {
    setActive(true);
    const requestId = ++userRequest.current;
    const key = current.current.scopeKey;
    try {
      dispatch(setLoading(true));
      const result = await read(dispatch, { url: apiUrls.engineeringTasks, params: { assignedTo } });
      if (mounted.current && requestId === userRequest.current && current.current.scopeKey === key && current.current.assignedTo === assignedTo &&
        store.getState().apiUrls?.urls?.engineeringTasks === apiUrls.engineeringTasks && store.getState().user?.userDetails === user) {
        setUserList({ key, rows: result });
      }
    } catch (err) {
      console.error("Error fetching user tasks:", err);
      dispatch(setError(err.message || "Failed to fetch user tasks"));
    } finally {
      if (mounted.current && requestId === userRequest.current) dispatch(setLoading(false));
    }
  }, [dispatch, store, apiUrls.engineeringTasks, user]);

  /**
   * Create a new engineering task
   */
  const createTask = useCallback(async (taskData) => {
    try {
      dispatch(setLoading(true));
      const result = await dispatch(
        postData({ 
          url: apiUrls.engineeringTasks, 
          data: taskData 
        })
      ).unwrap();
      dispatch(addTask(result));
      return result;
    } catch (err) {
      console.error("Error creating task:", err);
      dispatch(setError(err.message || "Failed to create task"));
      throw err;
    }
  }, [dispatch, apiUrls.engineeringTasks]);

  /**
   * Update an existing engineering task
   */
  const updateTask = useCallback(async (taskId, taskData) => {
    try {
      dispatch(setLoading(true));
      const result = await dispatch(
        patchData({ 
          url: `${apiUrls.engineeringTasks}?id=${taskId}`,
          data: taskData 
        })
      ).unwrap();
      dispatch(updateTaskAction(result));
      return result;
    } catch (err) {
      console.error("Error updating task:", err);
      dispatch(setError(err.message || "Failed to update task"));
      throw err;
    }
  }, [dispatch, apiUrls.engineeringTasks]);

  /**
   * Delete an engineering task
   */
  const deleteTask = useCallback(async (taskId) => {
    try {
      dispatch(setLoading(true));
      await dispatch(
        deleteData({ 
          url: `${apiUrls.engineeringTasks}?id=${taskId}`,
          deleteId: taskId 
        })
      ).unwrap();
      dispatch(deleteTaskAction(taskId));
    } catch (err) {
      console.error("Error deleting task:", err);
      dispatch(setError(err.message || "Failed to delete task"));
      throw err;
    }
  }, [dispatch, apiUrls.engineeringTasks]);

  /**
   * Fetch comments for a specific task
   */
  const fetchTaskComments = useCallback(async (taskId) => {
    try {
      const result = await read(dispatch, { url: apiUrls.engineeringTaskComments, params: { taskId } });
      dispatch(setTaskComments({ taskId, comments: result }));
      return result;
    } catch (err) {
      console.error("Error fetching task comments:", err);
      dispatch(setError(err.message || "Failed to fetch comments"));
      throw err;
    }
  }, [dispatch, apiUrls.engineeringTaskComments]);

  /**
   * Create a new comment for a task
   */
  const createComment = useCallback(async (commentData) => {
    try {
      const result = await dispatch(
        postData({ 
          url: apiUrls.engineeringTaskComments, 
          data: commentData 
        })
      ).unwrap();
      dispatch(addTaskComment(result));
      return result;
    } catch (err) {
      console.error("Error creating comment:", err);
      dispatch(setError(err.message || "Failed to create comment"));
      throw err;
    }
  }, [dispatch, apiUrls.engineeringTaskComments]);

  return {
    tasks: scoped ? (userList.key === scopeKey ? userList.rows : []) : tasks,
    comments,
    loading,
    error,
    filters,
    sortBy,
    sortOrder,
    fetchTasks,
    fetchUserTasks,
    createTask,
    updateTask,
    deleteTask,
    fetchTaskComments,
    createComment,
  };
};
